import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import type { SettingsOut } from "../../../api/client";
import { useHealth, useMe, useSettings, useUpdateSettings, useVersion } from "../../../api/hooks";
import { PageHead } from "../../../components/shell/Page";
import { LoadFailed } from "../../../components/ui/LoadState";
import { Icon, StatusDot, Toggle } from "../../../components/ui/primitives";
import { UnsavedGuard } from "../../../components/ui/UnsavedGuard";
import { type Key, t, tn } from "../../../i18n";
import { clock, formatDay, todayIn } from "../../../lib/time";
import { useUi } from "../../../state/ui";

export const Route = createFileRoute("/_app/admin/settings")({ component: SettingsPage });

type Values = Record<string, Record<string, unknown>>;

const CAPS = [
  { key: "monthly_usd", label: "admin.capMonthly" },
  { key: "daily_usd", label: "admin.capDaily" },
  { key: "person_daily_usd", label: "admin.capPersonDaily" },
  { key: "fast_turn_usd", label: "admin.capFastTurn" },
  { key: "deep_task_usd", label: "admin.capDeepTask" },
] as const;

const MODEL_ROWS = [
  { key: "fast", name: "admin.fastAnswers", hint: "admin.fastAnswersHint" },
  { key: "deep", name: "admin.deepReports", hint: "admin.deepReportsHint" },
  { key: "side", name: "admin.sideTasks", hint: "admin.sideTasksHint" },
  { key: "vision", name: "admin.vision", hint: "admin.visionHint" },
  { key: "embedding", name: "admin.searchIndex", hint: "admin.searchIndexHint" },
] as const;

const SWITCHES = [
  {
    key: "memory",
    field: "writes_enabled",
    name: "admin.memory",
    hint: "admin.memoryHint",
  },
  {
    key: "memory",
    field: "listening_enabled",
    name: "admin.listening",
    hint: "admin.listeningHint",
  },
  {
    key: "automations",
    field: "enabled",
    name: "admin.automations",
    hint: "admin.automationsHint",
  },
] as const;

const FEATURE_GROUPS: { label: Key; features: string[] }[] = [
  { label: "admin.deepReports", features: ["deep"] },
  { label: "admin.fastAnswers", features: ["fast"] },
  { label: "admin.automations", features: ["automation"] },
  { label: "admin.memoryAndEmbeddings", features: ["curator", "listener", "embedding"] },
  { label: "admin.titlesSummariesRouting", features: ["title", "summary", "classifier"] },
  { label: "admin.evals", features: ["eval"] },
];

function usd(v: number): string {
  return `$${v.toFixed(2)}`;
}

function cents(v: number): string {
  return v < 1 ? `${Math.max(0, Math.round(v * 100))}¢` : usd(v);
}

/** How a mail's sender is trusted (STATUS "R6 contract" 1, O14). */
const SENDER_CHECKS = [
  {
    value: "dmarc",
    label: "admin.senderDmarc",
    help: "admin.senderDmarcHelp",
  },
  {
    value: "dmarc_or_submission",
    label: "admin.senderSubmission",
    help: "admin.senderSubmissionHelp",
  },
] as const;

type Draft = {
  caps: Record<string, string>;
  switches: Record<string, boolean>;
  models: Record<string, string>;
  timezone: string;
  domains: string;
  uploadMb: string;
  previewPages: string;
  previewRows: string;
  emailOn: boolean;
  senderCheck: string;
};

function draftOf(values: Values): Draft {
  const caps = values["spend.caps"] ?? {};
  const models = values.models ?? {};
  const company = values.company ?? {};
  const email = values.email ?? {};
  return {
    caps: Object.fromEntries(CAPS.map((c) => [c.key, Number(caps[c.key] ?? 0).toFixed(2)])),
    switches: Object.fromEntries(
      SWITCHES.map((s) => [`${s.key}.${s.field}`, Boolean(values[s.key]?.[s.field])]),
    ),
    models: Object.fromEntries(MODEL_ROWS.map((m) => [m.key, String(models[m.key] ?? "")])),
    timezone: String(company.timezone ?? ""),
    domains: ((company.email_domains as string[] | undefined) ?? []).join(", "),
    uploadMb: String(values.files?.upload_limit_mb ?? ""),
    previewPages: String(values.files?.preview_pages ?? ""),
    previewRows: String(values.files?.preview_rows ?? ""),
    emailOn: Boolean(email.enabled),
    senderCheck: String(email.sender_check ?? "dmarc"),
  };
}

function patchOf(draft: Draft, original: Draft): Values {
  const patch: Values = {};
  const caps: Record<string, number> = {};
  for (const c of CAPS) {
    if (draft.caps[c.key] !== original.caps[c.key]) caps[c.key] = Number(draft.caps[c.key]);
  }
  if (Object.keys(caps).length) patch["spend.caps"] = caps;
  for (const s of SWITCHES) {
    const k = `${s.key}.${s.field}`;
    if (draft.switches[k] !== original.switches[k]) {
      patch[s.key] = { ...(patch[s.key] ?? {}), [s.field]: draft.switches[k] };
    }
  }
  const models: Record<string, string | null> = {};
  for (const m of MODEL_ROWS) {
    if (draft.models[m.key] !== original.models[m.key]) models[m.key] = draft.models[m.key] || null;
  }
  if (Object.keys(models).length) patch.models = models;
  const company: Record<string, unknown> = {};
  if (draft.timezone !== original.timezone) company.timezone = draft.timezone.trim();
  if (draft.domains !== original.domains) {
    company.email_domains = draft.domains
      .split(/[\s,]+/)
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean);
  }
  if (Object.keys(company).length) patch.company = company;
  const files: Record<string, number> = {};
  if (draft.uploadMb !== original.uploadMb)
    files.upload_limit_mb = Number.parseInt(draft.uploadMb, 10);
  if (draft.previewPages !== original.previewPages)
    files.preview_pages = Number.parseInt(draft.previewPages, 10);
  if (draft.previewRows !== original.previewRows)
    files.preview_rows = Number.parseInt(draft.previewRows.replace(/[,\s]/g, ""), 10);
  if (Object.keys(files).length) patch.files = files;
  const email: Record<string, unknown> = {};
  if (draft.emailOn !== original.emailOn) email.enabled = draft.emailOn;
  if (draft.senderCheck !== original.senderCheck) email.sender_check = draft.senderCheck;
  if (Object.keys(email).length) patch.email = email;
  return patch;
}

/** Email (PLAN §8.5; R6 contract 1): the switch, and how a sender is trusted. */
function EmailCard({ draft, setDraft }: { draft: Draft; setDraft: (d: Draft) => void }) {
  return (
    <section className="card-sec" data-testid="email-settings">
      <div className="cs-head">
        <span style={{ color: "var(--fg-3)", display: "inline-flex" }}>
          <Icon name="i-mail" />
        </span>
        <b>{t("admin.email")}</b>
        <span className="cs-sub">{t("admin.ownMailbox")}</span>
      </div>
      <div className="perm-row">
        <div className="grow">
          <div className="sname">{t("admin.email")}</div>
          <div className="smeta">{t("admin.emailHelp")}</div>
        </div>
        <Toggle
          checked={draft.emailOn}
          label={t("admin.email")}
          onChange={(on) => setDraft({ ...draft, emailOn: on })}
        />
      </div>
      <div className="src-row">
        <div className="grow">
          <div className="sname">
            <label htmlFor="sender-check">{t("admin.senderCheck")}</label>
          </div>
          <div className="smeta">{t("admin.senderCheckHelp")}</div>
        </div>
        <select
          id="sender-check"
          className="inp select"
          value={draft.senderCheck}
          onChange={(e) => setDraft({ ...draft, senderCheck: e.target.value })}
        >
          {SENDER_CHECKS.map((c) => (
            <option key={c.value} value={c.value}>
              {t(c.label)}
            </option>
          ))}
        </select>
      </div>
      <div className="cs-body legend">
        {SENDER_CHECKS.map((c) => (
          <div key={c.value}>
            <b>{t(c.label)}:</b> {t(c.help)}
          </div>
        ))}
      </div>
    </section>
  );
}

/** Files (R8 task 10, contract 7): the upload limit, and how much a preview shows. A change
 *  applies to new previews only. */
const FILE_FIELDS = [
  { key: "uploadMb", id: "upload-mb", label: "admin.uploadLimit", range: "1–200" },
  { key: "previewPages", id: "preview-pages", label: "admin.previewPages", range: "1–50" },
  {
    key: "previewRows",
    id: "preview-rows",
    label: "admin.previewRows",
    range: "50–10,000",
  },
] as const;

function FilesCard({ draft, setDraft }: { draft: Draft; setDraft: (d: Draft) => void }) {
  return (
    <section className="card-sec" data-testid="files-settings">
      <div className="cs-head">
        <span style={{ color: "var(--fg-3)", display: "inline-flex" }}>
          <Icon name="i-doc" />
        </span>
        <b>{t("admin.files")}</b>
        <span className="cs-sub">{t("admin.filesSub")}</span>
      </div>
      <div className="cs-body frm">
        <div className="row3">
          {FILE_FIELDS.map((f) => (
            <div key={f.key} className="fld">
              <label htmlFor={f.id}>{t(f.label)}</label>
              <input
                id={f.id}
                className="inp"
                inputMode="numeric"
                aria-describedby={`${f.id}-range files-help`}
                value={draft[f.key]}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
              />
              <div className="help" id={`${f.id}-range`}>
                {f.range}
              </div>
            </div>
          ))}
        </div>
        <div className="help" id="files-help">
          {t("admin.filesHelp")}
        </div>
      </div>
    </section>
  );
}

function SpendCard({ data }: { data: SettingsOut }) {
  const u = data.usage;
  const caps = (data.values["spend.caps"] ?? {}) as Record<string, number>;
  const monthCap = Number(caps.monthly_usd ?? 0);
  const pct = monthCap > 0 ? Math.min(100, Math.round((u.month_usd / monthCap) * 100)) : 0;
  const rows = FEATURE_GROUPS.map((g) => ({
    label: g.label,
    usd: g.features.reduce((s, f) => s + (u.by_feature[f] ?? 0), 0),
    calls: g.features.reduce((s, f) => s + (u.feature_calls[f] ?? 0), 0),
  })).filter((r) => r.calls > 0);
  // The 1st of next month: the spend month turns in company time (the server's caps), written
  // day-first, never "Sept" (R10-B2).
  const companyTz = useMe().data?.company_timezone;
  const [y = 0, m = 1] = todayIn(companyTz).split("-").map(Number);
  const first = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, "0")}-01`;
  const resets = formatDay(first, { month: "short", day: "numeric" });
  return (
    <section className="card-sec">
      <div className="cs-head">
        <span style={{ color: "var(--fg-3)", display: "inline-flex" }}>
          <Icon name="i-zap" />
        </span>
        <b>{t("admin.spend")}</b>
        <span className="cs-sub">{t("admin.spendSub", { date: resets })}</span>
      </div>
      <div className="cs-body">
        <div className="stats4">
          <div className="stat">
            <b>{usd(u.today_usd)}</b>
            <span>{t("admin.todayOf", { cap: usd(Number(caps.daily_usd ?? 0)) })}</span>
          </div>
          <div className="stat">
            <b>{usd(u.month_usd)}</b>
            <span>{t("admin.monthOf", { cap: usd(monthCap) })}</span>
          </div>
          <div className="stat">
            <b>{u.top_person_today ? usd(u.top_person_today.usd) : "—"}</b>
            <span>
              {u.top_person_today
                ? t("admin.topPersonToday", { name: u.top_person_today.name })
                : t("admin.noSpendToday")}
            </span>
          </div>
          <div className="stat">
            <b>{u.fast_avg_usd != null ? cents(u.fast_avg_usd) : "—"}</b>
            <span>{t("admin.avgFastAnswer")}</span>
          </div>
        </div>
        <div className="meter-row" style={{ margin: "12px 0 0" }}>
          <span>{t("admin.thisMonth")}</span>
          <div className="meter" role="img" aria-label={t("admin.capUsedAria", { pct })}>
            <span style={{ width: `${pct}%` }} />
          </div>
          <span className="num">{t("admin.pctOfCap", { pct })}</span>
        </div>
      </div>
      {rows.length === 0 && (
        <div className="perm-row">
          <div className="grow smeta">{t("admin.noSpendYet")}</div>
        </div>
      )}
      {rows.map((r) => (
        <div key={r.label} className="perm-row">
          <div className="grow">
            <div className="sname">{t(r.label)}</div>
            <div className="smeta">{tn("admin.calls", r.calls)}</div>
          </div>
          <span className="amt">{usd(r.usd)}</span>
        </div>
      ))}
    </section>
  );
}

const TOOL_NAMES: Record<string, string> = {
  redmine: "Redmine",
  github: "GitHub",
  outline: "Outline",
};

function HealthCard() {
  const { ready, components } = useHealth();
  const version = useVersion();
  const checked = clock(new Date().toISOString());
  const rows = components.data ?? [];
  // What each part is called; its state comes from the server's live check (R10-K9).
  const name = (c: string) =>
    c === "worker"
      ? t("admin.backgroundWorker")
      : c === "mail"
        ? t("admin.mail")
        : c.startsWith("sync:")
          ? t("admin.toolSync", { tool: TOOL_NAMES[c.slice(5)] ?? c.slice(5) })
          : c.startsWith("tool:")
            ? t("admin.toolNamed", { name: c.slice(5) })
            : c === "model"
              ? t("admin.models")
              : c;
  const tone = (s: string) =>
    s === "ok" ? "ok" : s === "degraded" ? "warn" : s === "off" ? "muted" : "danger";
  const said = (s: string) =>
    s === "ok"
      ? t("admin.healthOk")
      : s === "degraded"
        ? t("admin.healthDegraded")
        : s === "off"
          ? t("admin.off")
          : t("admin.healthDown");
  return (
    <section className="card-sec">
      <div className="cs-head">
        <span style={{ color: "var(--fg-3)", display: "inline-flex" }}>
          <Icon name="i-refresh" />
        </span>
        <b>{t("admin.systemHealth")}</b>
        <span className="grow" />
        <span className="cs-sub">{t("admin.checkedAt", { time: checked })}</span>
      </div>
      {version.data && (
        <div className="perm-row">
          <div className="grow">
            <div className="sname">{t("admin.version")}</div>
            {version.data.commit && (
              <div className="smeta">{t("admin.commit", { commit: version.data.commit })}</div>
            )}
          </div>
          <b className="ver">{version.data.version}</b>
        </div>
      )}
      <div className="perm-row">
        <div className="grow">
          <div className="sname">{t("admin.appAndDatabase")}</div>
        </div>
        {ready.data ? (
          <StatusDot tone={ready.data.status === "ok" ? "ok" : "danger"}>
            {ready.data.status === "ok" ? t("admin.healthOk") : t("admin.healthDown")}
          </StatusDot>
        ) : (
          <StatusDot tone="muted">{t("admin.checking")}</StatusDot>
        )}
      </div>
      {rows.map((c) => (
        <div key={c.component} className="perm-row">
          <div className="grow">
            <div className="sname">{name(c.component)}</div>
            <div className="smeta">
              {c.status === "ok" && c.last_ok_at
                ? t("admin.lastChecked", { time: clock(c.last_ok_at) })
                : (c.last_error ?? "")}
            </div>
          </div>
          <StatusDot tone={tone(c.status)}>{said(c.status)}</StatusDot>
        </div>
      ))}
    </section>
  );
}

function SettingsPage() {
  const settings = useSettings();
  const save = useUpdateSettings();
  const toast = useUi((s) => s.toast);
  // The values only: usage changes whenever anyone chats, and isn't on the form.
  const valuesJson = settings.data ? JSON.stringify(settings.data.values) : null;
  const original = useMemo(
    () => (valuesJson ? draftOf(JSON.parse(valuesJson) as Values) : null),
    [valuesJson],
  );
  const [draft, setDraft] = useState<Draft | null>(null);
  const shown = useRef<Draft | null>(null);
  const saving = useRef(false);
  // New values (a save, another admin, back on the tab): an untouched form follows them, an
  // edited one keeps the edits (review R10: a refetch wiped a typed cap).
  useEffect(() => {
    if (!original) return;
    const prev = shown.current;
    const keep = prev !== null && !saving.current;
    saving.current = false;
    shown.current = original;
    setDraft((d) => (keep && d && prev && Object.keys(patchOf(d, prev)).length > 0 ? d : original));
  }, [original]);

  if (!settings.data || !draft || !original) {
    // Loading or failed, said (R10-S3): never a blank page.
    return (
      <main className="sub main on no-panel" aria-label={t("shell.settings")}>
        <div className="page">
          {settings.isError ? (
            <LoadFailed query={settings} what={t("admin.whatSettings")} />
          ) : (
            <div className="load-state" role="status" aria-busy="true">
              {t("common.loadingWhat", { what: t("admin.whatSettings") })}
            </div>
          )}
        </div>
      </main>
    );
  }
  const data = settings.data;
  const values = data.values as Values;
  const patch = patchOf(draft, original);
  const dirty = Object.keys(patch).length > 0;
  const priced = Object.keys((values.prices?.models as Record<string, unknown>) ?? {});

  const onSave = () => {
    saving.current = true;
    save.mutate(patch, {
      onSuccess: () => toast(t("admin.settingsSaved")),
      onError: (e) => {
        saving.current = false;
        toast(e.message, true);
      },
    });
  };

  return (
    <main className="sub main on no-panel" aria-label={t("shell.settings")}>
      <UnsavedGuard dirty={dirty} />
      <div className="page">
        <PageHead
          title={t("shell.settings")}
          subtitle={t("admin.settingsSubtitle")}
          stack
          actions={
            <>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={!dirty}
                onClick={() => setDraft(original)}
              >
                {t("common.discard")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!dirty || save.isPending}
                onClick={onSave}
              >
                {t("admin.saveChanges")}
              </button>
            </>
          }
        />
        <div className="team-editor scroll">
          <div className="te-grid">
            <div>
              <SpendCard data={data} />

              <section className="card-sec">
                <div className="cs-head">
                  <span style={{ color: "var(--fg-3)", display: "inline-flex" }}>
                    <Icon name="i-shield" />
                  </span>
                  <b>{t("admin.spendCaps")}</b>
                  <span className="cs-sub">{t("admin.inUsd")}</span>
                </div>
                <div className="cs-body frm">
                  <div className="row2">
                    {CAPS.map((c) => (
                      <div key={c.key} className="fld">
                        <label htmlFor={`cap-${c.key}`}>{t(c.label)}</label>
                        <input
                          id={`cap-${c.key}`}
                          className="inp"
                          inputMode="decimal"
                          value={draft.caps[c.key] ?? ""}
                          onChange={(e) =>
                            setDraft({ ...draft, caps: { ...draft.caps, [c.key]: e.target.value } })
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <div className="help">{t("admin.capsHelp")}</div>
                </div>
              </section>

              <section className="card-sec">
                <div className="cs-head">
                  <span style={{ color: "var(--fg-3)", display: "inline-flex" }}>
                    <Icon name="i-spark" />
                  </span>
                  <b>{t("admin.models")}</b>
                  <span className="cs-sub">
                    {priced.length
                      ? t("admin.pricedModels", { n: priced.length })
                      : t("admin.noPrices")}
                  </span>
                </div>
                {MODEL_ROWS.map((m) => {
                  const current = draft.models[m.key] ?? "";
                  const options = Array.from(new Set([current, ...priced].filter(Boolean)));
                  return (
                    <div key={m.key} className="src-row">
                      <div className="grow">
                        <div className="sname">{t(m.name)}</div>
                        <div className="smeta">{t(m.hint)}</div>
                      </div>
                      <select
                        className="inp select"
                        aria-label={t(m.name)}
                        value={current}
                        disabled={options.length === 0}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            models: { ...draft.models, [m.key]: e.target.value },
                          })
                        }
                      >
                        {options.length === 0 && <option value="">{t("admin.notChosen")}</option>}
                        {options.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
                <div className="perm-row">
                  <div className="grow smeta">{t("admin.pricesHelp")}</div>
                </div>
              </section>

              <section className="card-sec">
                <div className="cs-head">
                  <span style={{ color: "var(--fg-3)", display: "inline-flex" }}>
                    <Icon name="i-sliders" />
                  </span>
                  <b>{t("admin.switches")}</b>
                  <span className="cs-sub">{t("admin.switchesSub")}</span>
                </div>
                {SWITCHES.map((s) => {
                  const k = `${s.key}.${s.field}`;
                  return (
                    <div key={k} className="perm-row">
                      <div className="grow">
                        <div className="sname">{t(s.name)}</div>
                        <div className="smeta">{t(s.hint)}</div>
                      </div>
                      <Toggle
                        checked={Boolean(draft.switches[k])}
                        label={t(s.name)}
                        onChange={(on) =>
                          setDraft({ ...draft, switches: { ...draft.switches, [k]: on } })
                        }
                      />
                    </div>
                  );
                })}
              </section>

              <EmailCard draft={draft} setDraft={setDraft} />

              <FilesCard draft={draft} setDraft={setDraft} />

              <section className="card-sec">
                <div className="cs-head">
                  <span style={{ color: "var(--fg-3)", display: "inline-flex" }}>
                    <Icon name="i-globe" />
                  </span>
                  <b>{t("admin.company")}</b>
                </div>
                <div className="cs-body frm">
                  <div className="fld">
                    <label htmlFor="company-tz">{t("admin.timeZone")}</label>
                    <input
                      id="company-tz"
                      className="inp"
                      value={draft.timezone}
                      onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
                    />
                  </div>
                  <div className="fld">
                    <label htmlFor="company-domains">{t("admin.emailDomains")}</label>
                    <input
                      id="company-domains"
                      className="inp"
                      value={draft.domains}
                      onChange={(e) => setDraft({ ...draft, domains: e.target.value })}
                    />
                    <div className="help">{t("admin.domainsHelp")}</div>
                  </div>
                </div>
              </section>
            </div>

            <aside className="preview-card">
              <HealthCard />
              <section className="card-sec">
                <div className="cs-head">
                  <b>{t("admin.testMode")}</b>
                  <span className="grow" />
                  <StatusDot tone={data.test_mode ? "warn" : "muted"}>
                    {data.test_mode ? t("admin.on") : t("admin.off")}
                  </StatusDot>
                </div>
                <div className="cs-body legend">
                  <div>{data.test_mode ? t("admin.testModeOn") : t("admin.testModeOff")}</div>
                  <div>{t("admin.testModeFenced")}</div>
                </div>
              </section>
            </aside>
          </div>
        </div>
      </div>
    </main>
  );
}
